import React, { useState } from 'react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/Card';
import { CheckCircle2, Building2, MapPin, CreditCard, Upload } from 'lucide-react';
import { cn } from '../lib/utils';
const STEPS = [{
  id: 1,
  title: 'Business Info',
  icon: Building2
}, {
  id: 2,
  title: 'Categories',
  icon: CheckCircle2
}, {
  id: 3,
  title: 'Locations',
  icon: MapPin
}, {
  id: 4,
  title: 'Payments',
  icon: CreditCard
}];
const CATEGORIES = [{
  value: 'home_mod',
  label: 'Home Modification & Aging-in-Place'
}, {
  value: 'adaptive_clothing',
  label: 'Adaptive Clothing & Accessories'
}, {
  value: 'medical_transport',
  label: 'Non-Emergency Medical Transportation'
}, {
  value: 'mobility',
  label: 'Mobility Solutions & Medical Devices'
}, {
  value: 'senior_living',
  label: 'Senior Living Communities'
}, {
  value: 'caregiving',
  label: 'In-Home Caregiving Services'
}, {
  value: 'meal_delivery',
  label: 'Senior Meal Delivery'
}];
export function MerchantOnboarding() {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  // Form State
  const [formData, setFormData] = useState({
    businessName: '',
    description: '',
    category: '',
    secondaryCategories: [] as string[],
    locations: [{
      address: '',
      city: '',
      state: '',
      zip: ''
    }],
    stripeConnected: false
  });
  const handleNext = () => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setStep(s => Math.min(s + 1, 4));
    }, 600);
  };
  const handleBack = () => setStep(s => Math.max(s - 1, 1));
  return <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8" data-id="element-436">
      <div className="max-w-3xl mx-auto" data-id="element-437">
        {/* Header */}
        <div className="text-center mb-12" data-id="element-438">
          <div className="flex justify-center mb-4" data-id="element-439">
            <div className="h-12 w-12 bg-[#218080] rounded-xl flex items-center justify-center text-white font-bold text-2xl" data-id="element-440">
              K
            </div>
          </div>
          <h1 className="text-3xl font-bold text-slate-900" data-id="element-441">
            Setup your Merchant Profile
          </h1>
          <p className="mt-2 text-slate-600" data-id="element-442">
            Join the Kithward network and reach thousands of senior customers.
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-8" data-id="element-443">
          <div className="flex items-center justify-between relative" data-id="element-444">
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-full h-0.5 bg-slate-200 -z-10" data-id="element-445" />
            {STEPS.map(s => {
            const isActive = s.id === step;
            const isCompleted = s.id < step;
            return <div key={s.id} className="flex flex-col items-center bg-slate-50 px-2" data-id="element-446">
                  <div className={cn('w-10 h-10 rounded-full flex items-center justify-center border-2 transition-colors duration-200', isActive ? 'border-[#218080] bg-white text-[#218080]' : isCompleted ? 'border-[#218080] bg-[#218080] text-white' : 'border-slate-300 bg-white text-slate-400')} data-id="element-447">
                    <s.icon className="w-5 h-5" data-id="element-448" />
                  </div>
                  <span className={cn('text-xs font-medium mt-2', isActive || isCompleted ? 'text-[#218080]' : 'text-slate-400')} data-id="element-449">
                    {s.title}
                  </span>
                </div>;
          })}
          </div>
        </div>

        {/* Form Content */}
        <Card className="shadow-lg border-0" data-id="element-450">
          <CardHeader data-id="element-451">
            <CardTitle data-id="element-452">{STEPS[step - 1].title}</CardTitle>
            <CardDescription data-id="element-453">
              {step === 1 && 'Tell us about your business.'}
              {step === 2 && 'Select the services you provide to seniors.'}
              {step === 3 && 'Where can customers find you?'}
              {step === 4 && 'Connect your bank account to receive payments.'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6" data-id="element-454">
            {/* Step 1: Business Info */}
            {step === 1 && <>
                <div className="grid gap-6 md:grid-cols-2" data-id="element-455">
                  <div className="col-span-2" data-id="element-456">
                    <div className="flex items-center justify-center w-full" data-id="element-457">
                      <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-slate-300 border-dashed rounded-lg cursor-pointer bg-slate-50 hover:bg-slate-100" data-id="element-458">
                        <div className="flex flex-col items-center justify-center pt-5 pb-6" data-id="element-459">
                          <Upload className="w-8 h-8 mb-3 text-slate-400" data-id="element-460" />
                          <p className="text-sm text-slate-500" data-id="element-461">
                            <span className="font-semibold" data-id="element-462">
                              Click to upload logo
                            </span>
                          </p>
                          <p className="text-xs text-slate-500" data-id="element-463">
                            SVG, PNG, JPG (min. 200x200px)
                          </p>
                        </div>
                        <input type="file" className="hidden" data-id="element-464" />
                      </label>
                    </div>
                  </div>
                  <Input label="Business Name" placeholder="e.g. Mobility Plus Inc." value={formData.businessName} onChange={e => setFormData({
                ...formData,
                businessName: e.target.value
              })} data-id="element-465" />
                  <Select label="Business Type" options={[{
                value: 'solo',
                label: 'Solo Provider'
              }, {
                value: 'franchise',
                label: 'Franchise'
              }, {
                value: 'corporate',
                label: 'Corporate Chain'
              }]} data-id="element-466" />
                  <div className="col-span-2" data-id="element-467">
                    <label className="text-sm font-medium text-slate-700 mb-2 block" data-id="element-468">
                      Description
                    </label>
                    <textarea className="w-full min-h-[100px] rounded-md border border-slate-300 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#218080]" placeholder="Describe your services for senior customers..." value={formData.description} onChange={e => setFormData({
                  ...formData,
                  description: e.target.value
                })} data-id="element-469" />
                  </div>
                  <Input label="Tax ID / EIN" placeholder="XX-XXXXXXX" data-id="element-470" />
                  <Input label="Website" placeholder="https://" data-id="element-471" />
                </div>
              </>}

            {/* Step 2: Categories */}
            {step === 2 && <div className="space-y-6" data-id="element-472">
                <Select label="Primary Category" placeholder="Select primary service category" options={CATEGORIES} value={formData.category} onChange={e => setFormData({
              ...formData,
              category: e.target.value
            })} helperText="This cannot be changed later." data-id="element-473" />

                <div className="space-y-3" data-id="element-474">
                  <label className="text-sm font-medium text-slate-700" data-id="element-475">
                    Secondary Categories (Optional, max 3)
                  </label>
                  <div className="grid gap-3 md:grid-cols-2" data-id="element-476">
                    {CATEGORIES.map(cat => <label key={cat.value} className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer hover:bg-slate-50" data-id="element-477">
                        <input type="checkbox" className="mt-1 h-4 w-4 rounded border-slate-300 text-[#218080] focus:ring-[#218080]" disabled={formData.category === cat.value} data-id="element-478" />
                        <span className="text-sm text-slate-700" data-id="element-479">
                          {cat.label}
                        </span>
                      </label>)}
                  </div>
                </div>
              </div>}

            {/* Step 3: Locations */}
            {step === 3 && <div className="space-y-6" data-id="element-480">
                {formData.locations.map((loc, idx) => <div key={idx} className="p-4 border border-slate-200 rounded-lg bg-slate-50 relative" data-id="element-481">
                    <h4 className="font-medium text-sm mb-4" data-id="element-482">
                      Location {idx + 1}
                    </h4>
                    <div className="grid gap-4 md:grid-cols-2" data-id="element-483">
                      <Input label="Address" className="col-span-2" placeholder="123 Main St" data-id="element-484" />
                      <Input label="City" placeholder="New York" data-id="element-485" />
                      <div className="grid grid-cols-2 gap-4" data-id="element-486">
                        <Input label="State" placeholder="NY" data-id="element-487" />
                        <Input label="ZIP" placeholder="10001" data-id="element-488" />
                      </div>
                    </div>
                  </div>)}
                <Button variant="outline" className="w-full border-dashed" onClick={() => setFormData({
              ...formData,
              locations: [...formData.locations, {
                address: '',
                city: '',
                state: '',
                zip: ''
              }]
            })} data-id="element-489">
                  + Add Another Location
                </Button>
              </div>}

            {/* Step 4: Stripe Connect */}
            {step === 4 && <div className="text-center py-8" data-id="element-490">
                <div className="mx-auto w-16 h-16 bg-[#635BFF]/10 rounded-full flex items-center justify-center mb-6" data-id="element-491">
                  <span className="text-[#635BFF] font-bold text-2xl" data-id="element-492">S</span>
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2" data-id="element-493">
                  Connect with Stripe
                </h3>
                <p className="text-slate-500 mb-8 max-w-md mx-auto" data-id="element-494">
                  Kithward uses Stripe to securely process commission payments.
                  You'll be redirected to Stripe to complete your setup.
                </p>

                {!formData.stripeConnected ? <Button className="bg-[#635BFF] hover:bg-[#534be0] text-white w-full max-w-sm" onClick={() => setFormData({
              ...formData,
              stripeConnected: true
            })} data-id="element-495">
                    Connect Stripe Account
                  </Button> : <div className="flex flex-col items-center animate-in fade-in zoom-in duration-300" data-id="element-496">
                    <div className="flex items-center gap-2 text-green-600 font-medium mb-4" data-id="element-497">
                      <CheckCircle2 className="w-5 h-5" data-id="element-498" />
                      Account Connected Successfully
                    </div>
                    <p className="text-sm text-slate-500" data-id="element-499">
                      Account ID: acct_123456789
                    </p>
                  </div>}
              </div>}
          </CardContent>

          <CardFooter className="flex justify-between border-t border-slate-100 pt-6" data-id="element-500">
            <Button variant="ghost" onClick={handleBack} disabled={step === 1} data-id="element-501">
              Back
            </Button>
            <Button onClick={handleNext} isLoading={isLoading} className="bg-[#218080] hover:bg-[#1a6666]" data-id="element-502">
              {step === 4 ? formData.stripeConnected ? 'Complete Setup' : 'Skip for now' : 'Continue'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>;
}